import { useEffect, useState, Component } from "react";
import { createPortal } from "react-dom";
import { crewOpsApi } from "@/api";
import {
  Search, CheckCircle, AlertTriangle, XCircle, X, Shield, Award,
  Plane, BookOpen, Clock, ChevronRight, RefreshCw, MapPin,
  User, Star, AlertCircle, TrendingUp, Activity, Calendar
} from "lucide-react";

// ─── Error Boundary ───────────────────────────────────────────────────────
class ProfileErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error("CrewProfile render error:", error, info); }
  render() {
    if (this.state.error) {
      const isModal = this.props.asModal;
      if (isModal) {
        // Show as a proper modal overlay so the user sees something
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-card border border-border rounded-sm p-8 max-w-md w-full mx-4 text-center shadow-2xl">
              <AlertTriangle size={36} className="text-amber-400 mx-auto mb-3" />
              <div className="text-base font-bold text-foreground mb-1">Profile failed to load</div>
              <div className="text-xs text-muted-foreground mb-2 font-mono break-all">{this.state.error?.message || "Unexpected render error"}</div>
              <div className="text-xs text-muted-foreground mb-4">This crew profile encountered a data rendering issue.</div>
              <div className="flex gap-2 justify-center">
                <button onClick={() => this.setState({ error: null })}
                  className="px-4 py-1.5 border border-border rounded-sm text-xs text-muted-foreground hover:text-foreground transition-colors">
                  Retry
                </button>
                {this.props.onClose && (
                  <button onClick={this.props.onClose}
                    className="px-4 py-1.5 bg-primary/10 border border-primary/30 rounded-sm text-xs text-primary hover:bg-primary/20 transition-colors">
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      }
      return (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <AlertTriangle size={28} className="text-amber-400 mb-2" />
          <div className="text-xs font-semibold text-foreground mb-1">Section failed to render</div>
          <div className="text-xs text-muted-foreground mb-3">{this.state.error?.message || "Unknown error"}</div>
          <button onClick={() => this.setState({ error: null })}
            className="px-3 py-1.5 border border-border rounded-sm text-xs text-muted-foreground hover:text-foreground transition-colors">
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Shared Badge Helpers ─────────────────────────────────────────────────
const STATUS_COLORS = {
  valid:          "text-green-400 bg-green-500/10 border-green-500/30",
  expiring_soon:  "text-amber-400 bg-amber-500/10 border-amber-500/30",
  expired:        "text-red-400 bg-red-500/10 border-red-500/30",
  not_authorized: "text-zinc-500 bg-zinc-500/10 border-zinc-500/20",
  authorized:     "text-blue-400 bg-blue-500/10 border-blue-500/30",
};

function StatusBadge({ status, label }) {
  const cls = STATUS_COLORS[status] || STATUS_COLORS.valid;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm text-xs font-bold border ${cls}`}>
      {label || status}
    </span>
  );
}

function ScoreDisplay({ score }) {
  const color = score >= 81 ? "text-red-400" : score >= 61 ? "text-orange-400" : score >= 31 ? "text-amber-400" : "text-green-400";
  return (
    <div className="flex items-center gap-1.5">
      <div className="h-1.5 w-16 bg-secondary rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${score >= 81 ? "bg-red-500" : score >= 61 ? "bg-orange-500" : score >= 31 ? "bg-amber-500" : "bg-green-500"}`}
          style={{ width: `${score}%` }} />
      </div>
      <span className={`font-mono text-xs font-bold ${color}`}>{score}</span>
    </div>
  );
}

function FatigueStatusBadge({ score }) {
  if (score >= 81) return <span className="text-xs font-bold text-red-400 bg-red-500/10 border border-red-500/30 px-1.5 py-0.5 rounded-sm">DNA</span>;
  if (score >= 61) return <span className="text-xs font-bold text-orange-400 bg-orange-500/10 border border-orange-500/30 px-1.5 py-0.5 rounded-sm">HIGH RISK</span>;
  if (score >= 31) return <span className="text-xs font-bold text-amber-400 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded-sm">CAUTION</span>;
  return <span className="text-xs font-bold text-green-400 bg-green-500/10 border border-green-500/30 px-1.5 py-0.5 rounded-sm">FIT</span>;
}

// ─── Profile Modal Tabs ───────────────────────────────────────────────────
function TabButton({ label, active, count, onClick }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold border-b-2 transition-colors whitespace-nowrap ${
        active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
      }`}>
      {label}
      {count !== undefined && <span className={`px-1 py-0.5 rounded-sm text-xs ${active ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground"}`}>{count}</span>}
    </button>
  );
}

function InfoRow({ label, value, mono = false, highlight }) {
  return (
    <div className="flex justify-between items-start py-1.5 border-b border-border/30">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-xs font-semibold ${mono ? "font-mono" : ""} ${highlight || "text-foreground"} text-right max-w-48`}>{value ?? "—"}</span>
    </div>
  );
}

// ─── Tab 1: Overview ─────────────────────────────────────────────────────
function OverviewTab({ profile }) {
  if (!profile) return <div className="text-muted-foreground text-sm py-8 text-center">Profile data unavailable</div>;
  const lic = profile.licence;
  const hrs = profile.hours;
  const med = profile.medical;
  return (
    <div className="grid grid-cols-3 gap-4">
      {/* Personal & Employment */}
      <div className="space-y-1">
        <div className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-2">Identity & Employment</div>
        <InfoRow label="Employee ID"  value={profile.employee_id} mono />
        <InfoRow label="Full Name"    value={profile.name} />
        <InfoRow label="Rank"         value={profile.rank} />
        <InfoRow label="Base"         value={profile.base} mono />
        <InfoRow label="Primary Fleet" value={profile.primary_fleet} mono />
        <InfoRow label="Qualified Fleets" value={profile.qualified_fleets?.join(", ")} mono />
        <InfoRow label="Employment"   value={profile.employment_type} />
        <InfoRow label="Join Date"    value={profile.join_date} mono />
        <InfoRow label="Seniority"    value={profile.seniority_date} mono />
        <InfoRow label="HR Source"    value={profile.hr_source?.replace(/_/g," ") || "Live HR API"} />
      </div>

      {/* Licence & Medical */}
      <div className="space-y-1">
        <div className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-2">Licence & Medical</div>
        {lic ? (
          <>
            <InfoRow label="Licence Type"   value={lic.type} />
            <InfoRow label="Licence No."    value={lic.number} mono />
            <InfoRow label="Authority"      value={lic.authority} />
            <InfoRow label="Licence Expiry" value={lic.expiry} mono highlight={new Date(lic.expiry) < new Date(Date.now() + 90*86400000) ? "text-amber-400" : "text-green-400"} />
            <InfoRow label="ELP Level"      value={`Level ${lic.elp_level} (ICAO)`} mono />
            <InfoRow label="ELP Expiry"     value={lic.elp_expiry} mono />
            <div className="pt-2 border-t border-border/30 mt-2 mb-1">
              <div className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-2">Medical Certificate</div>
            </div>
            <InfoRow label="Medical Class"  value={med?.class || "Class 1"} />
            <InfoRow label="Medical Expiry" value={med?.expiry || profile.medical_expiry} mono highlight="text-green-400" />
            <InfoRow label="Restriction"    value={med?.restriction || "None"} />
          </>
        ) : (
          <div className="text-xs text-muted-foreground py-2">Cabin crew — no pilot licence</div>
        )}
      </div>

      {/* Flying Hours */}
      <div className="space-y-1">
        <div className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-2">Flying Hours & Limits</div>
        {hrs ? (
          <>
            <InfoRow label="Total Hours"     value={hrs.total_flying_hours?.toLocaleString()} mono highlight="text-blue-400" />
            <InfoRow label="Total PIC Hours" value={hrs.total_pic_hours?.toLocaleString()} mono />
            {Object.entries(hrs.type_hours || {}).map(([fl, h]) => (
              <InfoRow key={fl} label={`${fl} Hours`} value={h?.toLocaleString()} mono />
            ))}
            <div className="pt-2 mt-2 border-t border-border/30">
              <div className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-2">Duty Time (Running)</div>
            </div>
            <InfoRow label="7-Day FDP"     value={`${hrs.last_7_days_fdp_hours}h`}    mono highlight={hrs.last_7_days_fdp_hours > 60  ? "text-red-400" : "text-foreground"} />
            <InfoRow label="28-Day Block"  value={`${hrs.last_28_days_block_hours}h`} mono highlight={hrs.last_28_days_block_hours > 100 ? "text-red-400" : "text-foreground"} />
            <InfoRow label="28-Day FDP"    value={`${hrs.last_28_days_fdp_hours}h`}   mono />
            <InfoRow label="365-Day Block" value={`${hrs.last_365_days_block_hours}h`} mono highlight={hrs.last_365_days_block_hours > 900 ? "text-amber-400" : "text-foreground"} />
            <InfoRow label="Annual Leave"  value={`${hrs.annual_leave_balance_days} days`} />
            <InfoRow label="Sick Leave YTD" value={`${hrs.sick_leave_used_ytd} days`} />
          </>
        ) : <div className="text-xs text-muted-foreground py-2">No hours data</div>}
      </div>
    </div>
  );
}

// ─── Tab 2: Qualifications ────────────────────────────────────────────────
function QualificationsTab({ profile }) {
  const trs = Array.isArray(profile?.type_ratings) ? profile.type_ratings : [];
  const rec = profile?.recency;
  return (
    <div className="space-y-5">
      {/* Type Ratings */}
      <div>
        <div className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3">Type Ratings</div>
        {trs.length === 0 ? <p className="text-xs text-muted-foreground">No type ratings (cabin crew)</p> : (
          <div className="space-y-2">
            {trs.map((tr, i) => (
              <div key={i} className={`border rounded-sm p-3 ${tr.status === "expired" ? "border-red-500/30 bg-red-500/5" : tr.status === "expiring_soon" ? "border-amber-500/30 bg-amber-500/5" : "border-border bg-card/50"}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-black text-foreground text-sm">{tr.type}</span>
                    <StatusBadge status={tr.status} label={tr.status === "expired" ? "EXPIRED" : tr.status === "expiring_soon" ? "EXPIRING" : "VALID"} />
                    {tr.lvo_authorized && <span className="text-xs px-1.5 py-0.5 bg-blue-500/15 text-blue-400 border border-blue-500/30 rounded-sm font-bold">LVO</span>}
                    {tr.cat3_authorized && <span className="text-xs px-1.5 py-0.5 bg-purple-500/15 text-purple-400 border border-purple-500/30 rounded-sm font-bold">CAT III</span>}
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">Exp: {tr.expiry}</span>
                </div>
                <div className="grid grid-cols-4 gap-3 text-xs">
                  <div><div className="text-muted-foreground">Issue Date</div><div className="font-mono text-foreground">{tr.issue_date}</div></div>
                  <div><div className="text-muted-foreground">OXC Date</div><div className="font-mono text-foreground">{tr.opc_date}</div></div>
                  <div><div className="text-muted-foreground">OXC Expiry</div><div className={`font-mono ${new Date(tr.opc_expiry) < new Date() ? "text-red-400" : "text-foreground"}`}>{tr.opc_expiry}</div></div>
                  <div><div className="text-muted-foreground">PC Expiry</div><div className="font-mono text-foreground">{tr.pc_expiry}</div></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recency */}
      {rec && (
        <div>
          <div className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3">Recency & Currency</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-muted-foreground mb-2 font-semibold">Last 3 Landings</div>
              <div className="space-y-1">
                {rec.last_three_landings?.map((l, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-1.5 bg-card/50 border border-border/50 rounded-sm text-xs">
                    <span className="w-4 h-4 rounded-sm bg-blue-600 text-white flex items-center justify-center font-bold text-xs">{i+1}</span>
                    <span className="font-mono text-foreground">{l.date}</span>
                    <span className="font-mono text-primary">{l.type}</span>
                    <span className="font-mono text-muted-foreground">{l.airport}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <InfoRow label="Night Currency"          value={rec.night_currency_date} mono />
              <InfoRow label="Instrument Approaches (90d)" value={rec.instrument_approaches_90d} mono />
              <InfoRow label="Last Line Check"         value={rec.last_line_check_date} mono />
              <InfoRow label="Line Check Expiry"       value={rec.last_line_check_expiry} mono highlight={new Date(rec.last_line_check_expiry) < new Date() ? "text-red-400" : "text-green-400"} />
              <InfoRow label="IR Expiry"               value={rec.instrument_rating_expiry} mono />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab 3: Airport Clearances ────────────────────────────────────────────
function AirportClearancesTab({ profile }) {
  const clr = Array.isArray(profile?.airport_clearances) ? profile.airport_clearances : [];
  const groups = {
    "CAT II/III Operations": clr.filter(c => c && ["cat_ii","cat_iii"].includes(c.clearance_type)),
    "High-Altitude / Restricted": clr.filter(c => c && ["high_altitude","restricted_airspace"].includes(c.clearance_type)),
    "International Routes":  clr.filter(c => c && c.clearance_type === "international"),
  };

  return (
    <div className="space-y-5">
      {Object.entries(groups).map(([grp, items]) => (
        <div key={grp}>
          <div className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3">{grp}</div>
          <div className="overflow-hidden border border-border rounded-sm">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-secondary/30">
                  {["IATA","Name","Type","Level/Country","Authorized","Expiry","Notes"].map(h => (
                    <th key={h} className="text-left px-3 py-2 text-xs text-muted-foreground font-bold uppercase tracking-widest">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((c, i) => (
                  <tr key={i} className="border-t border-border/40 hover:bg-primary/5">
                    <td className="px-3 py-2 font-mono font-bold text-foreground">{c.iata}</td>
                    <td className="px-3 py-2 text-muted-foreground">{c.name}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className="px-1 py-0.5 bg-secondary rounded-sm text-muted-foreground font-mono text-xs">{c.clearance_type?.replace(/_/g," ")}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{c.level || c.country || (c.elevation_ft ? `${c.elevation_ft?.toLocaleString()} ft` : "—")}</td>
                    <td className="px-3 py-2">
                      {c.authorized
                        ? <span className="flex items-center gap-1 text-green-400"><CheckCircle size={11} /> Yes</span>
                        : <span className="flex items-center gap-1 text-zinc-500"><XCircle size={11} /> No</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{c.expiry || "—"}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{c.special_notes || c.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Tab 4: Special Authorizations ───────────────────────────────────────
function AuthorizationsTab({ profile }) {
  const auths = Array.isArray(profile?.special_authorizations) ? profile.special_authorizations : [];
  const groups = {
    "Navigation & Performance": ["ETOPS_120","ETOPS_180","MNPS","PBN","RVSM"],
    "Low Visibility Operations": ["LVO","CAT_II","CAT_III_A","CAT_III_B","CPDLC"],
    "Instructor / Examiner": ["TRI","TRE","SFI","SFE"],
    "Other Authorizations": ["DGR_CAT6","SECURITY_INSTR"],
  };
  return (
    <div className="space-y-5">
      {Object.entries(groups).map(([grp, types]) => {
        const items = auths.filter(a => types.includes(a.type));
        if (!items.length) return null;
        return (
          <div key={grp}>
            <div className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3">{grp}</div>
            <div className="grid grid-cols-1 gap-2">
              {items.map((a, i) => (
                <div key={i} className={`flex items-start justify-between p-3 border rounded-sm ${a.authorized ? "border-border bg-card/50" : "border-border/30 bg-background/30 opacity-60"}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${a.authorized ? "bg-green-500" : "bg-zinc-600"}`} />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-xs text-foreground">{a.type}</span>
                        {a.authorized
                          ? <span className="text-xs px-1.5 py-0.5 bg-green-500/15 text-green-400 border border-green-500/30 rounded-sm">AUTHORIZED</span>
                          : <span className="text-xs px-1.5 py-0.5 bg-zinc-500/10 text-zinc-500 border border-zinc-500/20 rounded-sm">NOT AUTHORIZED</span>}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{a.description}</div>
                      {a.authorized && <div className="text-xs text-muted-foreground mt-0.5 font-mono">{a.authority} · Exp: {a.expiry}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Tab 5: Training Records ──────────────────────────────────────────────
function TrainingTab({ profile }) {
  const recs = Array.isArray(profile?.training_records) ? profile.training_records : [];
  const expired   = recs.filter(r => r?.status === "expired").length;
  const expiring  = recs.filter(r => r?.status === "expiring_soon").length;
  const valid     = recs.filter(r => r?.status === "valid").length;

  return (
    <div>
      <div className="flex gap-3 mb-4">
        {[["Valid", valid, "text-green-400"], ["Expiring", expiring, "text-amber-400"], ["Expired", expired, "text-red-400"]].map(([l,v,c]) => (
          <div key={l} className="flex items-center gap-2 px-3 py-1.5 bg-card border border-border rounded-sm text-xs">
            <span className={`font-bold font-mono text-base ${c}`}>{v}</span>
            <span className="text-muted-foreground">{l}</span>
          </div>
        ))}
      </div>
      <div className="overflow-hidden border border-border rounded-sm">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-secondary/30">
              {["Module","Regulatory Ref","Completed","Expiry","Days Left","Grade","Status"].map(h => (
                <th key={h} className="text-left px-3 py-2 text-xs text-muted-foreground font-bold uppercase tracking-widest">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {recs.length === 0 ? <tr><td colSpan={7} className="text-center py-4 text-muted-foreground text-xs">No training records</td></tr> :
            [...recs].sort((a,b) => (a?.days_remaining ?? 0) - (b?.days_remaining ?? 0)).map((r, i) => (
              <tr key={i} className={`border-t border-border/40 ${r.status === "expired" ? "bg-red-500/5" : r.status === "expiring_soon" ? "bg-amber-500/5" : ""}`}>
                <td className="px-3 py-2">
                  <div className="font-bold text-foreground">{r.module_name}</div>
                  <div className="font-mono text-muted-foreground">{r.module_code}</div>
                </td>
                <td className="px-3 py-2 font-mono text-muted-foreground">{r.regulatory_ref}</td>
                <td className="px-3 py-2 font-mono text-muted-foreground">{r.completed_date}</td>
                <td className="px-3 py-2 font-mono">{r.expiry_date}</td>
                <td className={`px-3 py-2 font-mono font-bold ${r.days_remaining <= 0 ? "text-red-400" : r.days_remaining <= 60 ? "text-amber-400" : "text-green-400"}`}>
                  {r.days_remaining <= 0 ? "EXPIRED" : `${r.days_remaining}d`}
                </td>
                <td className="px-3 py-2 text-muted-foreground">{r.grade}</td>
                <td className="px-3 py-2">
                  <StatusBadge status={r.status} label={r.status === "expiring_soon" ? "EXPIRING" : (r.status || "valid").toUpperCase()} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Profile Modal ────────────────────────────────────────────────────────
function CrewProfileModal({ crewBasic, onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!crewBasic?.id) return;
    setLoading(true);
    crewOpsApi.getCrewFullProfile(crewBasic.id)
      .then(r => setProfile(r.data))
      .catch(() => setProfile(crewBasic))
      .finally(() => setLoading(false));
  }, [crewBasic?.id]);

  const p = profile || crewBasic;
  const trainingRecs = Array.isArray(p?.training_records) ? p.training_records : [];
  const typeRatings  = Array.isArray(p?.type_ratings)     ? p.type_ratings     : [];
  const airportClrs  = Array.isArray(p?.airport_clearances) ? p.airport_clearances : [];
  const specAuths    = Array.isArray(p?.special_authorizations) ? p.special_authorizations : [];

  const expiredTraining  = trainingRecs.filter(r => r?.status === "expired").length;
  const expiringTraining = trainingRecs.filter(r => r?.status === "expiring_soon").length;
  const expiredTR        = typeRatings.filter(r => r?.status === "expired").length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="crew-profile-modal">
      <div className="bg-card border border-border rounded-sm w-full max-w-5xl max-h-[92vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-border shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-sm bg-primary/10 border border-primary/20 flex items-center justify-center text-xl font-black text-primary">
              {crewBasic.name?.[0]}
            </div>
            <div>
              <div className="text-xs text-muted-foreground tracking-widest uppercase font-bold mb-0.5">{crewBasic.employee_id} · {crewBasic.rank}</div>
              <h2 className="text-xl font-black text-foreground">{crewBasic.name}</h2>
              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                <MapPin size={10} /> {crewBasic.base}
                <span className="mx-1">·</span>
                <Plane size={10} /> {crewBasic.qualified_fleets?.join(", ")}
                {expiredTR > 0 && <span className="ml-2 flex items-center gap-1 text-red-400"><AlertTriangle size={10} /> {expiredTR} rating expired</span>}
                {expiredTraining > 0 && <span className="flex items-center gap-1 text-red-400"><AlertCircle size={10} /> {expiredTraining} training overdue</span>}
                {expiringTraining > 0 && <span className="flex items-center gap-1 text-amber-400"><AlertCircle size={10} /> {expiringTraining} expiring soon</span>}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1"><X size={18} /></button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-0 px-5 border-b border-border shrink-0 overflow-x-auto">
          <TabButton label="Overview"            active={tab==="overview"}  onClick={() => setTab("overview")} />
          <TabButton label="Qualifications"      active={tab==="quals"}     onClick={() => setTab("quals")}
            count={expiredTR > 0 ? `${expiredTR} exp` : undefined} />
          <TabButton label="Airport Clearances"  active={tab==="airports"}  onClick={() => setTab("airports")}
            count={airportClrs.filter(c => c?.authorized).length} />
          <TabButton label="Authorizations"      active={tab==="auths"}     onClick={() => setTab("auths")}
            count={specAuths.filter(a => a?.authorized).length} />
          <TabButton label="Training Records"    active={tab==="training"}  onClick={() => setTab("training")}
            count={expiredTraining + expiringTraining > 0 ? `${expiredTraining + expiringTraining} action` : trainingRecs.length} />
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
              <RefreshCw size={16} className="animate-spin mr-2" /> Loading full profile...
            </div>
          ) : (
            <ProfileErrorBoundary key={tab}>
              {tab === "overview"  && <OverviewTab profile={p} />}
              {tab === "quals"     && <QualificationsTab profile={p} />}
              {tab === "airports"  && <AirportClearancesTab profile={p} />}
              {tab === "auths"     && <AuthorizationsTab profile={p} />}
              {tab === "training"  && <TrainingTab profile={p} />}
            </ProfileErrorBoundary>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-border shrink-0">
          <div className="text-xs text-muted-foreground">
            Profile v{p?.profile_version || "1.0"} · Source: {p?.hr_source?.replace(/_/g," ") || "HR System"}
          </div>
          <div className="flex items-center gap-3">
            <ScoreDisplay score={crewBasic.fatigue_score || 0} />
            <FatigueStatusBadge score={crewBasic.fatigue_score || 0} />
            <button onClick={onClose} className="px-4 py-1.5 border border-border text-xs text-muted-foreground rounded-sm hover:text-foreground transition-colors">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Crew Master Page ─────────────────────────────────────────────────────
export default function CrewMaster() {
  const [crew, setCrew] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [filterRank, setFilterRank] = useState("");
  const [filterBase, setFilterBase] = useState("");

  useEffect(() => {
    crewOpsApi.getCrew()
      .then(r => setCrew(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCrew([]))
      .finally(() => setLoading(false));
  }, []);

  const ranks = [...new Set(crew.map(c => c.rank).filter(Boolean))];
  const bases = [...new Set(crew.map(c => c.base).filter(Boolean))];

  const filtered = crew.filter(c =>
    (!search || (c.name || "").toLowerCase().includes(search.toLowerCase()) || (c.employee_id || "").toLowerCase().includes(search.toLowerCase())) &&
    (!filterRank || c.rank === filterRank) &&
    (!filterBase || c.base === filterBase)
  );

  return (
    <div className="p-6 space-y-5 fade-in-up" data-testid="crew-master">
      <div>
        <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">Crew Rostering · Module A</div>
        <h1 className="text-2xl font-black font-heading text-foreground">Crew Master</h1>
        <p className="text-xs text-muted-foreground mt-1">Master crew records — licences, type ratings, airport clearances, authorizations & training</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-3">
        {[
          ["Total Crew",     filtered.length,                                                                           "text-primary"],
          ["Captains",       filtered.filter(c => c.rank === "Captain" || c.rank === "Training Captain").length,        "text-blue-400"],
          ["First Officers", filtered.filter(c => c.rank === "First Officer").length,                                  "text-blue-300"],
          ["Cabin Crew",     filtered.filter(c => !["Captain","Training Captain","First Officer"].includes(c.rank)).length, "text-purple-400"],
        ].map(([lbl, val, col]) => (
          <div key={lbl} className="bg-card border border-border rounded-sm p-4">
            <div className={`text-3xl font-mono font-medium ${col}`}>{val}</div>
            <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">{lbl}</div>
            {(filterBase || filterRank) && (
              <div className="text-xs text-muted-foreground/50 mt-0.5 font-mono">
                {[filterBase, filterRank].filter(Boolean).join(" · ")}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or ID..."
            className="bg-card border border-border rounded-sm pl-9 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary w-56" />
        </div>
        <select value={filterRank} onChange={e => setFilterRank(e.target.value)}
          className="bg-card border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
          <option value="">All Ranks</option>
          {ranks.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={filterBase} onChange={e => setFilterBase(e.target.value)}
          className="bg-card border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
          <option value="">All Bases</option>
          {bases.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>

      {/* Crew Table */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="crew-master-table">
          <thead>
            <tr className="border-b border-border">
              {["Employee","Rank","Base","Fleet","Clearances","Fatigue","Fitness","Medical",""].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-xs tracking-widest uppercase text-muted-foreground font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-8 text-muted-foreground text-xs">Loading crew data...</td></tr>
            ) : filtered.map(c => (
              <tr key={c.id} className="border-b border-border/50 hover:bg-primary/5 cursor-pointer transition-colors"
                onClick={() => setSelected(c)} data-testid={`crew-row-${c.employee_id}`}>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-sm bg-primary/10 flex items-center justify-center text-xs font-black text-primary shrink-0">{c.name?.[0]}</div>
                    <div>
                      <div className="text-xs font-semibold text-foreground">{c.name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{c.employee_id}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">{c.rank}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-foreground">{c.base}</td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-1 flex-wrap">
                    {c.qualified_fleets?.map(f => (
                      <span key={f} className="text-xs font-mono text-primary bg-primary/10 px-1 py-0.5 rounded-sm">{f}</span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1">
                    {c.airport_clearances?.filter(a => a?.authorized).slice(0,3).map((a,i) => (
                      <span key={i} className="text-xs font-mono bg-secondary text-muted-foreground px-1 py-0.5 rounded-sm">{a.iata}</span>
                    ))}
                    {(c.airport_clearances?.filter(a => a?.authorized).length || 0) > 3 && (
                      <span className="text-xs text-muted-foreground">+{c.airport_clearances.filter(a => a?.authorized).length - 3}</span>
                    )}
                    {(!c.airport_clearances || c.airport_clearances.filter(a => a?.authorized).length === 0) && (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2.5"><ScoreDisplay score={c.fatigue_score || 0} /></td>
                <td className="px-3 py-2.5"><FatigueStatusBadge score={c.fatigue_score || 0} /></td>
                <td className="px-3 py-2.5">
                  {c.medical_expiry
                    ? <span className="text-xs text-green-400 flex items-center gap-1"><CheckCircle size={11} /> {c.medical_expiry}</span>
                    : <span className="text-xs text-green-400 flex items-center gap-1"><CheckCircle size={11} /> Valid</span>}
                </td>
                <td className="px-3 py-2.5">
                  <span className="flex items-center gap-1 text-xs text-primary hover:text-primary/80">
                    Full Profile <ChevronRight size={11} />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && createPortal(
        <ProfileErrorBoundary key={selected.id} asModal onClose={() => setSelected(null)}>
          <CrewProfileModal crewBasic={selected} onClose={() => setSelected(null)} />
        </ProfileErrorBoundary>,
        document.body
      )}
    </div>
  );
}
